import React from 'react';
import { Map } from 'immutable';
import { Separator } from 'draft-js-inline-toolbar-plugin';
import Styles from '@plone/volto/config/RichTextEditor/Styles';
import Plugins from '@plone/volto/config/RichTextEditor/Plugins';
import Blocks from '@plone/volto/config/RichTextEditor/Blocks';
// import FromHTMLCustomBlockFn from '@plone/volto/config/RichTextEditor/FromHTML';
import createLinkPlugin from '@plone/volto/components/manage/AnchorPlugin';

import {
  richTextRenderers,
  toolbarButtons,
} from './RichTextEditor/ToolbarConfig';
import { FONT_STYLE_MAP } from './RichTextEditor/TextSizeButton';

/***** DraftJs *****/

const addBreaklines = (children) =>
  children.map((child) => {
    return child[1].map((child) => [
      <React.Fragment key={child}>
        {child}
        <br />
      </React.Fragment>,
    ]);
  });

const visitBlockRenderMap = Map({
  'align-center': {
    element: 'p',
  },
  'blockquote-big': {
    element: 'blockquote',
  },
  callout: {
    element: 'p',
  },
});

const visitListBlockTypes = ['blockquote-big'];

export const VisitDraftJSConfig = (config) => {
  const visitBlockStyleFn = (baseBlockStyleFn) => {
    return (contentBlock) => {
      const type = contentBlock?.getType();
      let r = '';
      if (type) {
        r = baseBlockStyleFn(contentBlock) || '';
        r = r?.length > 0 ? ' ' : r;

        const styles = {
          'align-center': 'align-center',
          callout: 'callout',
          'callout-bg-cyan': 'callout bg-cyan',
          'callout-bg-light-red': 'callout bg-light-red',
          'callout-bg-peach': 'callout bg-peach',
          'callout-bg-light-green': 'callout bg-light-green',
          'callout-bg-light-grey': 'callout bg-light-grey',
          'blockquote-big': 'big',
        };

        r += styles[type] ?? '';
      }

      return r;
    };
  };

  config.settings.richtextEditorSettings = (props) => {
    const { plugins } = Plugins(props);
    const linkPlugin = createLinkPlugin({ libraries: props });
    const buttons = Styles(props);

    const {
      BoldButton,
      ItalicButton,
      HeadlineTwoButton,
      HeadlineThreeButton,
      UnorderedListButton,
      OrderedListButton,
      BlockquoteButton,
      // CalloutButton,
    } = buttons;

    const inlineToolbarButtons = [
      BoldButton,
      ItalicButton,
      linkPlugin.LinkButton,
      Separator,
      HeadlineTwoButton,
      HeadlineThreeButton,
      UnorderedListButton,
      OrderedListButton,
      BlockquoteButton,
      // CalloutButton, removed old callout button
    ];

    const { extendedBlockRenderMap, blockStyleFn, listBlockTypes } = Blocks(
      props,
    );
    const visitToolbarButtons = toolbarButtons(props);

    return {
      extendedBlockRenderMap: extendedBlockRenderMap.merge(visitBlockRenderMap),
      blockStyleFn: visitBlockStyleFn(blockStyleFn),
      listBlockTypes: listBlockTypes.concat(visitListBlockTypes),
      richTextEditorPlugins: plugins,
      richTextEditorInlineToolbarButtons: [
        visitToolbarButtons.AlignCenterButton,
        visitToolbarButtons.TextSizeButton,
        Separator,
        ...inlineToolbarButtons,
        visitToolbarButtons.BlockquoteBigButton,
        Separator,
        visitToolbarButtons.CalloutsButton,
      ],
      // FromHTMLCustomBlockFn,
      customStyleMap: {
        ...FONT_STYLE_MAP,
      },
    };
  };

  const calloutBlocks = [
    { type: 'callout', className: '' },
    { type: 'callout-bg-cyan', className: 'bg-cyan' },
    { type: 'callout-bg-light-red', className: 'bg-light-red' },
    { type: 'callout-bg-peach', className: 'bg-peach' },
    { type: 'callout-bg-light-green', className: 'bg-light-green' },
    { type: 'callout-bg-light-grey', className: 'bg-light-grey' },
  ];

  const renderHTMLBlock = (child) => {
    return child.map((subchild) => {
      if (Array.isArray(subchild)) {
        return subchild.map((subchildren) => {
          if (typeof subchildren === 'string') {
            const last = subchildren.split('\n').length - 1;
            return subchildren.split('\n').map((item, index) => (
              <React.Fragment key={index}>
                {item}
                {index !== last && <br />}
              </React.Fragment>
            ));
          } else {
            return subchildren;
          }
        });
      } else {
        return subchild;
      }
    });
  };

  // Genera dinamicamente i renderer callout
  const calloutRenderers = Object.fromEntries(
    calloutBlocks.map(({ type, className }) => [
      type,
      (children, { keys }) =>
        children.map((child, i) => (
          <p
            id={keys[i]}
            key={keys[i]}
            className={'callout ' + className}
            role="note"
          >
            {renderHTMLBlock(child)}
          </p>
        )),
    ]),
  );

  config.settings.richtextViewSettings.ToHTMLRenderers = {
    ...config.settings.richtextViewSettings.ToHTMLRenderers,
    inline: {
      ...config.settings.richtextViewSettings.ToHTMLRenderers.inline,
      ...richTextRenderers.TextSizeRenderer,
    },
    blocks: {
      ...config.settings.richtextViewSettings.ToHTMLRenderers.blocks,
      ...richTextRenderers.AlignCenterToHtmlRenderer,
      blockquote: (children, { keys }) => (
        <blockquote key={keys[0]}>{addBreaklines(children)}</blockquote>
      ),
      'blockquote-big': (children, { keys }) => {
        return (
          <blockquote className="big" key={keys[0]}>
            {addBreaklines(children)}
          </blockquote>
        );
      },
      ...calloutRenderers,
    },
  };
};
